import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { useInventoryService } from '../../../../apis/inventory/useInventoryService';
import {
  TInventoryItem,
  TInventoryItemListApiResponse,
  TInventoryItemParams,
  TCreateInventoryItemRequest,
  TUpdateInventoryItemRequest,
} from '../../../../apis/inventory/inventory.api.interfaces';

type TQueryError = IFHError<{ message: string }>;
type TMutationError = IFHError<{ message: string }>;

// Query keys
export const getInventoryItemListKey = 'get-inventory-item-list';
export const getInventoryItemByIdKey = 'get-inventory-item-by-id';

// ===================== LIST HOOK =====================
export const useInventoryItemListHook = (
  params: TInventoryItemParams,
  options?: UseQueryOptions<TInventoryItemListApiResponse, TQueryError>
): UseQueryResult<TInventoryItemListApiResponse, TQueryError> => {
  const { getInventoryItemList } = useInventoryService();

  return useQuery<TInventoryItemListApiResponse, TQueryError>({
    queryKey: [getInventoryItemListKey, params],
    queryFn: () => getInventoryItemList(params),
    enabled: true,
    ...options,
  });
};

// ===================== GET BY ID HOOK =====================
export const useInventoryItemByIdHook = (
  id: string,
  options?: UseQueryOptions<TInventoryItem, TQueryError>
): UseQueryResult<TInventoryItem, TQueryError> => {
  const { getInventoryItemById } = useInventoryService();

  return useQuery<TInventoryItem, TQueryError>({
    queryKey: [getInventoryItemByIdKey, id],
    queryFn: () => getInventoryItemById(id),
    enabled: Boolean(id),
    ...options,
  });
};

// ===================== CREATE HOOK =====================
export const useCreateInventoryItemHook = (
  options?: UseMutationOptions<
    TInventoryItem,
    TMutationError,
    TCreateInventoryItemRequest
  >
): UseMutationResult<
  TInventoryItem,
  TMutationError,
  TCreateInventoryItemRequest
> => {
  const { createInventoryItem } = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation<
    TInventoryItem,
    TMutationError,
    TCreateInventoryItemRequest
  >({
    mutationFn: (item) => createInventoryItem(item),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getInventoryItemListKey] });
    },
    ...options,
  });
};

// ===================== UPDATE HOOK =====================
export const useUpdateInventoryItemHook = (
  options?: UseMutationOptions<
    TInventoryItem,
    TMutationError,
    { id: string; data: TUpdateInventoryItemRequest }
  >
): UseMutationResult<
  TInventoryItem,
  TMutationError,
  { id: string; data: TUpdateInventoryItemRequest }
> => {
  const { updateInventoryItem } = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation<
    TInventoryItem,
    TMutationError,
    { id: string; data: TUpdateInventoryItemRequest }
  >({
    mutationFn: ({ id, data }) => updateInventoryItem(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [getInventoryItemListKey] });
      queryClient.invalidateQueries({
        queryKey: [getInventoryItemByIdKey, variables.id],
      });
    },
    ...options,
  });
};

// ===================== DELETE HOOK =====================
export const useDeleteInventoryItemHook = (
  options?: UseMutationOptions<void, TMutationError, string>
): UseMutationResult<void, TMutationError, string> => {
  const { deleteInventoryItem } = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation<void, TMutationError, string>({
    mutationFn: (id) => deleteInventoryItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getInventoryItemListKey] });
    },
    ...options,
  });
};
